import React from 'react';
import VirtualizedAutocomplete from '../../../../../components/Dropdown/VirtualizedAutocomplete';

const RenderFields = ({
  fields,
  formData,
  handleChange,
  handleClassChange,
  subclassOptions = [],
  readOnly = false,
  fieldTransformers = {},
  validationErrors = {},
  buildings = [], // Added buildings as a prop
  selectedCustomer = {},
  selectedManagement = {},
}) => {
  return fields.map((field) => {
    const isFieldReadOnly = readOnly || field.key === 'deviceId';

    if (field.key === 'buildingId') {
      return (
        <div key={field.key} className="flex flex-col w-full">
          <div className="flex items-center w-full pb-2">
            <label className="font-semibold p-2 w-1/3 h-16 flex items-center">
              {field.label || 'Building'}
              <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
            </label>
              <div className="w-2/3">
                <VirtualizedAutocomplete
                    options={buildings || []}
                    value={
                      buildings.find(
                        (building) => building.buildingID === formData.buildingId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      handleChange({
                        target: {
                          name: field.key,
                          value: newValue ? newValue.buildingID : '',
                        },
                      });
                    }}
                    optionKey="buildingID"
                    optionLabel="buildingReference"
                    disabled={isFieldReadOnly}
                  />
            </div>
          </div>
          {validationErrors[field.key] && (
            <div className="text-red-500 text-sm mt-1 ml-2">
              {validationErrors[field.key]}
            </div>
          )}
        </div>
      );
    }

    if (field.key === 'customerId') {
      return (
        <div key={field.key} className="flex flex-col w-full">
            <div className="flex items-center w-full pb-2">
                <label className="font-semibold p-2 w-1/3 h-16 flex items-center">
                    {field.label || 'Customer'}
                    <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
                </label>
                <label className="border border-gray-300 flex-grow p-2 rounded-md bg-gray-100">
                    {selectedCustomer?.customerName || 'N/A'}
                </label>
            </div>
        </div>
      );
    }
    
    if (field.key === 'managementId') {
      return (
        <div key={field.key} className="flex flex-col w-full">
          <div className="flex items-center w-full pb-2">
            <label className="font-semibold p-2 w-1/3 h-16 flex items-center">
              {field.label || 'Management'}
              <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
            </label>
            <label className="border border-gray-300 flex-grow p-2 rounded-md bg-gray-100">
              {selectedManagement?.managementName || 'N/A'}
            </label>
          </div>
        </div>
      );
    }

    if (field.type === 'select' && field.options) {
      // Use RenderFieldsDropdown for select fields
      return (
        <RenderFieldsDropdown
          key={field.key}
          fields={[field]}
          formData={formData}
          handleChange={handleChange}
          handleClassChange={handleClassChange}
          subclassOptions={subclassOptions}
          readOnly={readOnly}
          validationErrors={validationErrors}
        />
      );
    }

    if (field.type === 'checkbox') {
      // Handle checkbox fields
      return (
        <div key={field.key} className="flex items-center w-full pb-2">
          <label
            className="font-semibold p-2 w-1/3 h-16 flex items-center"
            style={{
              lineHeight: '1.2',
              wordBreak: 'break-word',
            }}
          >
            {field.label}
            <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
          </label>
          <input
            name={field.key}
            type="checkbox"
            checked={!!formData[field.key]}
            onChange={handleChange}
            disabled={isFieldReadOnly}
            className={`${isFieldReadOnly ? 'cursor-not-allowed' : ''}`}
            style={{ width: '20px', height: '20px' }}
          />
        </div>
      );
    }

    // Handle standard fields
    return (
      <div key={field.key} className="flex flex-col w-full">
        <div className="flex items-center w-full pb-2">
          <label
            className="font-semibold p-2 w-1/3 h-16 flex items-center"
            style={{
              lineHeight: '1.2',
              wordBreak: 'break-word',
            }}
          >
            {field.label}
            <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
          </label>
          <input
            name={field.key}
            type={field.type || 'text'}
            className={`border ${
              validationErrors[field.key]
                ? 'border-red-500'
                : 'border-gray-300'
            } flex-grow p-2 rounded-md ${
              isFieldReadOnly ? 'bg-gray-100' : ''
            }`}
            value={
              fieldTransformers[field.key]
                ? fieldTransformers[field.key](formData[field.key])
                : formData[field.key] || ''
            }
            onChange={handleChange}
            readOnly={isFieldReadOnly}
          />
        </div>
        {validationErrors[field.key] && (
          <div className="text-red-500 text-sm mt-1">
            {validationErrors[field.key]}
          </div>
        )}
      </div>
    );
  });
};

const RenderFieldsDropdown = ({
  fields,
  formData,
  handleChange,
  handleClassChange,
  subclassOptions = [],
  readOnly = false,
  validationErrors = {},
}) => {
  return fields.map((field) => {
    if (field.key === 'deviceClass') {
      return (
        <div key={field.key} className="flex flex-col w-full">
          <div className="flex justify-between items-center w-full pb-2">
            <label className="font-semibold p-2 w-1/3 h-16 flex items-center">
              {field.label}
              <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
            </label>
            <select
              name="deviceClass"
              className={`border w-2/3 border-gray-300 flex-grow p-2 rounded-md my-auto ${readOnly ? 'bg-gray-100' : ''}`}
              value={formData.deviceClass || ''}
              onChange={handleClassChange}
              disabled={readOnly}
            >
              <option value="">Select a class</option>
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }
    
    if (field.key === 'deviceSubclass') {
      return (
        <div key={field.key} className="flex flex-col w-full">
          <div className="flex justify-between items-center w-full pb-2">
            <label className="font-semibold p-2 w-1/3 h-16 flex items-center">
              {field.label}
              <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
            </label>
            <select
              name="deviceSubclass"
              className={`border w-2/3 border-gray-300 flex-grow p-2 rounded-md my-auto ${readOnly ? 'bg-gray-100' : ''}`}
              value={formData.deviceSubclass || ''}
              onChange={handleChange}
              disabled={readOnly}
            >
              <option value="">Select a subclass</option>
              {subclassOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }
    return (
      <div key={field.key} className="flex flex-col w-full">
        <div className="flex justify-between items-center w-full pb-2">
          <label
            className="font-semibold p-2 w-1/3 h-16 flex items-center"
            style={{
              lineHeight: '1.2',
              wordBreak: 'break-word'
            }}
          >
            {field.label}:
            <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
          </label>
          <select
            name={field.key}
            className={`border w-2/3 ${
              validationErrors[field.key] ? 'border-red-500' : 'border-gray-300'
            } flex-grow p-2 rounded-md my-auto ${readOnly ? 'bg-gray-100' : ''}`}
            value={formData[field.key] || ''}
            onChange={handleChange}
            disabled={readOnly}
          >
            <option value="">Select an option</option>
              {field.options.map((option) => (
                  <option key={option.value} value={option.value}>
                  {option.label}
                  </option>
              ))}
          </select>
        </div>
        {validationErrors[field.key] && (
          <div className="text-red-500 text-sm mt-1 ml-2">
            {validationErrors[field.key]}
          </div>
        )}
      </div>
    );
  });
};

export { RenderFields, RenderFieldsDropdown };
